function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2026-05-25 21:18:10 +0200 (Mon, 25 May 2026) $
//  Author    : Albert Charlier, Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 163265 $
//  Purpose   : pdf viewer
// =============================================================================
// ---Third-party imports
import React, { Component, PureComponent } from 'react';
import { Header, Menu, Dimmer, Loader, Segment } from 'semantic-ui-react';
import { PDFDocument, PDFPage, PDFOutline, ReflexContainer, ReflexSplitter, ReflexElement } from 'uix/chunks';
// ---Framework imports
import log from 'uix/logging';
import connect from 'uix/connect';
import { isNil } from 'ldash';
import { AutoPortal, HeaderSegment } from 'uix/components';
import { prettyBytes } from 'uix/utils';
import { useState, usePersistent } from 'uix/hooks';
// ---Local imports
import styles from './styles.css';
// ---Globals
class PromiseCancelledException extends Error {
    constructor(message, type){
        super(message, type);
        this.name = 'PromiseCancelledException';
        this.message = message;
        this.type = type;
    }
}
const makeCancellable = (promise)=>{
    let isCancelled = false;
    const wrappedPromise = new Promise((resolve, reject)=>{
        promise.then((...args)=>isCancelled ? reject(new PromiseCancelledException('Promise cancelled')) : resolve(...args), (error)=>isCancelled ? reject(new PromiseCancelledException('Promise cancelled')) : reject(error));
    });
    return {
        promise: wrappedPromise,
        cancel () {
            isCancelled = true;
        }
    };
};
const cancelRunningTask = (runningTask)=>{
    if (!runningTask || !runningTask.cancel) {
        return;
    }
    runningTask.cancel();
};
const isDefined = (variable)=>typeof variable !== 'undefined';
class Ref {
    toString() {
        let str = `${this.num}R`;
        if (this.gen !== 0) {
            str += this.gen;
        }
        return str;
    }
    constructor({ num, gen }){
        this.num = num;
        this.gen = gen;
    }
}
class OutlineItem extends PureComponent {
    renderSubitems() {
        const { item, ...otherProps } = this.props;
        if (!item.items || !item.items.length) {
            return null;
        }
        const { items: subitems } = item;
        return /*#__PURE__*/ _jsx("ul", {
            children: subitems.map((subitem, subitemIndex)=>/*#__PURE__*/ _jsx(OutlineItem, {
                    item: subitem,
                    ...otherProps
                }, typeof subitem.destination === 'string' ? subitem.destination : subitemIndex))
        });
    }
    render() {
        const { item } = this.props;
        /* eslint-disable jsx-a11y/anchor-is-valid */ return /*#__PURE__*/ _jsxs("li", {
            children: [
                /*#__PURE__*/ _jsx("a", {
                    href: "#",
                    onClick: this.onClick,
                    children: item.title
                }),
                this.renderSubitems()
            ]
        });
    }
    constructor(...args){
        super(...args), _define_property(this, "getDestination", async ()=>{
            const { item, pdf } = this.props;
            if (!isDefined(this.destination)) {
                if (typeof item.dest === 'string') {
                    this.destination = await pdf.getDestination(item.dest);
                } else {
                    this.destination = item.dest;
                }
            }
            return this.destination;
        }), _define_property(this, "getPageIndex", async ()=>{
            const { pdf } = this.props;
            if (!isDefined(this.pageIndex)) {
                const destination = await this.getDestination();
                if (destination) {
                    const [ref] = destination;
                    this.pageIndex = await pdf.getPageIndex(new Ref(ref));
                }
            }
            return this.pageIndex;
        }), _define_property(this, "getPageNumber", async ()=>{
            if (!isDefined(this.pageNumber)) {
                this.pageNumber = await this.getPageIndex() + 1;
            }
            return this.pageNumber;
        }), _define_property(this, "onClick", async (event)=>{
            const { onClick } = this.props;
            event.preventDefault();
            const pageIndex = await this.getPageIndex();
            const pageNumber = await this.getPageNumber();
            if (onClick) {
                onClick({
                    pageIndex,
                    pageNumber
                });
            }
        });
    }
}
const MIN_OUTLINE = 2;
class _PDF extends Component {
    componentWillUnmount() {
        cancelRunningTask(this.runningTask);
    }
    render() {
        let { apiurl, otid, pageIndex, showOutline, rotate, onLoadProgress } = this.props;
        let { outline } = this.state;
        let url = `${apiurl}/download/pdf/${otid}`;
        let loader = /*#__PURE__*/ _jsx(Dimmer, {
            active: true,
            inverted: true,
            children: /*#__PURE__*/ _jsx(Loader, {
                inverted: true,
                content: "Loading..."
            })
        });
        let outlineActive = showOutline && outline;
        return /*#__PURE__*/ _jsx("div", {
            style: {
                display: 'flex',
                flex: 1
            },
            children: /*#__PURE__*/ _jsxs(ReflexContainer, {
                orientation: "vertical",
                children: [
                    outlineActive ? /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(ReflexElement, {
                                flex: "0.3",
                                children: this.outline()
                            }, "outline"),
                            /*#__PURE__*/ _jsx(ReflexSplitter, {
                                className: styles.splitter
                            })
                        ]
                    }) : null,
                    /*#__PURE__*/ _jsx(ReflexElement, {
                        flex: outlineActive ? 0.7 : 1.0,
                        children: /*#__PURE__*/ _jsx(PDFDocument, {
                            renderMode: "canvas",
                            file: url,
                            onLoadSuccess: this.onLoadSuccess,
                            onLoadProgress: onLoadProgress,
                            loading: loader,
                            children: /*#__PURE__*/ _jsx(PDFPage, {
                                pageIndex: pageIndex,
                                loading: "",
                                rotate: rotate
                            })
                        })
                    }, "page")
                ]
            })
        });
    }
    constructor(...args){
        super(...args), _define_property(this, "state", {
            pdf: null,
            outline: null
        }), _define_property(this, "onLoadSuccess", (pdf)=>{
            //~ log('loaded PDF with %o pages %O', pdf.numPages, pdf)
            this.setState({
                pdf
            });
            this.loadOutline(pdf);
            let { onLoadSuccess } = this.props;
            if (onLoadSuccess) {
                onLoadSuccess(pdf);
            }
        }), _define_property(this, "loadOutline", async (pdf)=>{
            const { onOutlineLoaded } = this.props;
            this.setState((prevState)=>{
                if (!prevState.outline) {
                    return null;
                }
                return {
                    outline: null
                };
            });
            try {
                const cancellable = makeCancellable(pdf.getOutline());
                this.runningTask = cancellable;
                const outline = await cancellable.promise;
                this.setState({
                    outline
                });
                if (onOutlineLoaded) {
                    onOutlineLoaded(outline);
                }
            } catch (error) {
                log.error(error);
                this.setState({
                    outline: null
                });
                if (onOutlineLoaded) {
                    onOutlineLoaded(null);
                }
            }
        }), _define_property(this, "outline", ()=>{
            let { onOutlineItemClicked } = this.props;
            let { outline, pdf } = this.state;
            return /*#__PURE__*/ _jsx("ul", {
                className: styles.outline,
                children: outline.map((item, itemIndex)=>/*#__PURE__*/ _jsx(OutlineItem, {
                        item: item,
                        pdf: pdf,
                        onClick: onOutlineItemClicked
                    }, typeof item.destination === 'string' ? item.destination : itemIndex))
            });
        });
    }
}
_define_property(_PDF, "storeMap", 'config.apiurl');
const PDF = connect('PDF', _PDF);
export const PDFViewer = ({ request })=>{
    /* render PDF documents
   */ let [state, _setState] = useState({
        numPages: -1,
        showOutline: true,
        rotate: 0,
        scale: 0.5,
        loadingSize: {
            loaded: 0,
            total: 0
        }
    });
    let [pageIndexes, setPageIndexes] = usePersistent('pageIndexes', {});
    let setState = (data)=>_setState((state)=>({
                ...state,
                ...data
            }));
    //~ static partStyle = {display: 'flex'}
    let setPageIndex = (otid, idx)=>{
        setPageIndexes({
            ...pageIndexes,
            [otid]: idx
        });
    };
    let onLoadSuccess = (pdf)=>{
        setState({
            numPages: pdf.numPages
        });
    };
    let onOutlineLoaded = (outline)=>{
        setState({
            outline
        });
    };
    let onLoadProgress = ({ loaded, total })=>{
        //~ log.info(loaded, total)
        setState({
            loadingSize: {
                loaded,
                total
            }
        });
    };
    let onOutlineItemClicked = ({ pageNumber })=>{
        setPageIndex(request.id, pageNumber - 1);
    };
    let { numPages, showOutline, rotate, outline, loadingSize } = state;
    let pageIndex = pageIndexes[request.id] || 0;
    let outlineDisabled = !outline || outline.length <= MIN_OUTLINE;
    let menu = /*#__PURE__*/ _jsxs(Menu, {
        children: [
            /*#__PURE__*/ _jsx(Menu.Item, {
                title: "Show Outline",
                active: showOutline,
                disabled: outlineDisabled,
                icon: "content",
                onClick: ()=>setState({
                        showOutline: !showOutline
                    })
            }),
            /*#__PURE__*/ _jsx(Menu.Item, {
                title: "Rotate Left",
                icon: "undo",
                onClick: ()=>setState({
                        rotate: (rotate + 270) % 360
                    })
            }),
            /*#__PURE__*/ _jsx(Menu.Item, {
                title: "Rotate Right",
                icon: "redo",
                onClick: ()=>setState({
                        rotate: (rotate + 90) % 360
                    })
            }),
            /*#__PURE__*/ _jsx(Menu.Item, {
                icon: "left arrow",
                title: "Next Page",
                disabled: pageIndex == 0,
                onClick: ()=>setPageIndex(request.id, pageIndex - 1)
            }),
            /*#__PURE__*/ _jsx(Menu.Item, {
                content: `${pageIndex + 1}/${numPages}`
            }),
            /*#__PURE__*/ _jsx(Menu.Item, {
                icon: "right arrow",
                title: "Next Page",
                disabled: pageIndex >= numPages - 1,
                onClick: ()=>setPageIndex(request.id, pageIndex + 1)
            })
        ]
    });
    showOutline = showOutline && !outlineDisabled;
    let loading = loadingSize.total > loadingSize.loaded;
    let subheader = `${prettyBytes(loadingSize.total)}`;
    if (loading) {
        subheader = `${subheader} (${prettyBytes(loadingSize.loaded)})`;
    }
    return /*#__PURE__*/ _jsx(HeaderSegment, {
        header: "PDF",
        flex: true,
        overflowInner: true,
        subheader: subheader,
        loading: loading,
        icon: "file pdf outline",
        menu: menu,
        children: /*#__PURE__*/ _jsx(PDF, {
            otid: request.id,
            showOutline: showOutline,
            pageIndex: pageIndexes[request.id] || 0,
            onOutlineItemClicked: onOutlineItemClicked,
            onOutlineLoaded: onOutlineLoaded,
            onLoadSuccess: onLoadSuccess,
            onLoadProgress: onLoadProgress,
            rotate: rotate
        })
    });
};
